/**
 * 줄 위 빠른 작업 (2026-08-21) — 답장 · 팝업보기 · 휴지통 · 읽음처리.
 *
 * ★★ 2026-08-26 — **치워 둔 메일함(휴지통·스팸)에서는 할 일이 다르다**(사용자 지시).
 *    버린 편지에 답장·이동·읽음은 뜻이 없고, 휴지통에서 '휴지통' 은 이미 있는 곳에 또 넣는 모양이다.
 *      · 휴지통 → **복원 · 영구삭제**
 *      · 스팸함 → **복원 · 휴지통**
 *    선택바(MailSelectionBar)와 **같은 짝**이다 — 한 줄로 하든 여러 줄로 하든 할 일이 같아야 한다.
 *
 * ★ 날짜·크기 자리를 **덮는다**(자리를 새로 만들지 않는다). 자리를 만들면 마우스를 올릴 때마다
 *   줄이 흔들려, 누르려던 아이콘이 손 밑에서 움직인다. 왼쪽은 투명 그라데이션으로 흐린다.
 * ★ 이동은 **옮길 메일함이 있을 때만** 그린다(migration 114). 구 서버에는 폴더가 없어
 *   `onMove` 가 안 오고, 그러면 버튼도 안 나온다 — 있는데 안 되는 버튼보다 없는 편이 낫다.
 * ★ 밖에서 온 메일은 보낸 사람의 우리 쪽 id 가 없어 답장을 만들 수 없다 → 그 줄만 답장을 끈다.
 */

import { Mail, MailOpen, Reply, ExternalLink, FolderInput, Trash2, RotateCcw } from '../../icons';
import { tMail } from '../../lib/mail-i18n';
import type { MailListItem } from '../../api/mail';

export type MailRowMode = 'normal' | 'trash' | 'spam';

interface MailRowActionsProps {
  message: MailListItem;
  busy: boolean;
  /** 어느 메일함을 보고 있나 — 치워 둔 함에서는 할 일이 둘뿐이다. */
  mode?: MailRowMode;
  /** 휴지통·스팸에서 되돌리기. 그 두 함에서만 온다. */
  onRestore?: (m: MailListItem) => void;
  /** 휴지통에서 영원히 지우기. 옛 서버에는 길이 없어 안 올 수 있다. */
  onPurge?: (m: MailListItem) => void;
  onReply: (m: MailListItem) => void;
  onPopout: (m: MailListItem) => void;
  onTrash: (m: MailListItem) => void;
  onToggleRead: (m: MailListItem) => void;
  /** 옮길 메일함이 생긴 뒤(migration 114)에만 온다 — 없으면 버튼을 안 그린다. */
  onMove?: (m: MailListItem) => void;
}

export function MailRowActions(props: MailRowActionsProps) {
  const { message, busy, mode = 'normal', onReply, onPopout, onTrash, onToggleRead, onMove, onRestore, onPurge } = props;
  const external = message.sender.isExternal === true || !message.sender.id;
  const stop = (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); };

  if (mode === 'trash' || mode === 'spam') {
    return (
      <span className="mail-row-actions">
        {onRestore && (
          <button
            type="button"
            className="mail-row-action"
            aria-label={tMail('mail.bulk.restore')}
            title={tMail('mail.bulk.restore')}
            disabled={busy}
            onClick={(e) => { stop(e); onRestore(message); }}
          >
            <RotateCcw size={15} aria-hidden />
          </button>
        )}
        {mode === 'trash'
          ? onPurge && (
            <button
              type="button"
              className="mail-row-action mail-row-action--danger"
              aria-label={tMail('mail.bulk.purge')}
              title={tMail('mail.bulk.purge')}
              disabled={busy}
              onClick={(e) => { stop(e); onPurge(message); }}
            >
              <Trash2 size={15} aria-hidden />
            </button>
          )
          : (
            <button
              type="button"
              className="mail-row-action mail-row-action--danger"
              aria-label={tMail('mail.row.trash')}
              title={tMail('mail.row.trash')}
              disabled={busy}
              onClick={(e) => { stop(e); onTrash(message); }}
            >
              <Trash2 size={15} aria-hidden />
            </button>
          )}
      </span>
    );
  }

  return (
    <span className="mail-row-actions">
      <button
        type="button"
        className="mail-row-action"
        aria-label={tMail('mail.row.reply')}
        title={external ? tMail('mail.row.reply_external_na') : tMail('mail.row.reply')}
        disabled={busy || external}
        onClick={(e) => { stop(e); onReply(message); }}
      >
        <Reply size={15} aria-hidden />
      </button>
      <button
        type="button"
        className="mail-row-action"
        aria-label={tMail('mail.row.popout')}
        title={tMail('mail.row.popout')}
        onClick={(e) => { stop(e); onPopout(message); }}
      >
        <ExternalLink size={15} aria-hidden />
      </button>
      {onMove && (
        <button
          type="button"
          className="mail-row-action"
          aria-label={tMail('mail.row.move')}
          title={tMail('mail.row.move')}
          disabled={busy}
          onClick={(e) => { stop(e); onMove(message); }}
        >
          <FolderInput size={15} aria-hidden />
        </button>
      )}
      <button
        type="button"
        className="mail-row-action mail-row-action--danger"
        aria-label={tMail('mail.row.trash')}
        title={tMail('mail.row.trash')}
        disabled={busy}
        onClick={(e) => { stop(e); onTrash(message); }}
      >
        <Trash2 size={15} aria-hidden />
      </button>
      <button
        type="button"
        className="mail-row-action"
        aria-label={message.isRead ? tMail('mail.row.mark_unread') : tMail('mail.row.mark_read')}
        title={message.isRead ? tMail('mail.row.mark_unread') : tMail('mail.row.mark_read')}
        disabled={busy}
        onClick={(e) => { stop(e); onToggleRead(message); }}
      >
        {/*
          * ★ 2026-08-26 — 봉투를 뒤집었다(사용자 지적: "읽음표시가 반대").
          *   여태는 **누르면 될 상태**를 그렸는데(읽은 줄엔 닫힌 봉투 = 안 읽음으로 만들기),
          *   목록에서 아이콘은 **지금 그 메일이 어떤가**로 먼저 읽힌다. 그래서 읽은 줄은 열린
          *   봉투, 안 읽은 줄은 닫힌 봉투로 둔다. 무엇을 하는 버튼인지는 툴팁이 말해 준다.
          */}
        {message.isRead ? <MailOpen size={15} aria-hidden /> : <Mail size={15} aria-hidden />}
      </button>
    </span>
  );
}
